import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BosDurum, Buton, cn, Iskelet, Kart, Rozet, anMetni } from "@koclukpanel/ui";
import {
  ApiHatasi,
  elleYedekAl,
  yedekDogrulamalari,
  yedekKonsolu,
  yedekTatbikati,
  type YedekHedefi,
  type YedekKosusu,
  type YedekSagligi,
} from "./api";

/**
 * YEDEKLEME KONSOLU — F-YED
 *
 * ================================================================
 * BU EKRAN "YESIL" GOSTERMEK ICIN YAZILMADI
 * ================================================================
 * Bir yedek konsolunun en tehlikeli hali, her sey yolundaymis gibi
 * gorunup felaket gununde bos cikmasidir. O yuzden burada:
 *
 *   · hic yedek yoksa "iyi" DEGIL "olculemedi" yaziyor
 *   · alinmis ama dogrulanmamis yedek "iyi" sayilmiyor
 *   · "hic denenmedi" ile "denendi, olmadi" ayri gosteriliyor
 *
 * Ucu de sunucudan geliyor; ekran kendi karar vermiyor.
 */

const SAGLIK_ADI: Record<YedekSagligi, string> = {
  IYI: "İyi",
  DIKKAT: "Dikkat",
  KRITIK: "Kritik",
  OLCULEMEDI: "Ölçülemedi",
};

/**
 * ================================================================
 * "OLCULEMEDI" YESIL DE DEGIL KIRMIZI DA
 * ================================================================
 * Yesil olsaydi hic yedek alinmamis bir sistem saglikli gorunurdu.
 * Kirmizi olsaydi yeni kurulmus bir sistem her acilista alarm
 * verir, alarm da bir sure sonra bakilmaz olurdu. Notr bir renk
 * dogru olani soyluyor: bilmiyoruz.
 */
const SAGLIK_SINIFI: Record<YedekSagligi, string> = {
  IYI: "bg-basarili-500/15 text-onay-700",
  DIKKAT: "bg-uyari-500/15 text-uyari-800",
  KRITIK: "bg-hata-500/15 text-hata-700",
  OLCULEMEDI: "bg-notr-100 text-notr-600",
};

function boyut(bayt: number | null): string {
  // "0 B" ile "olculmedi" ayri: olculmeyen "—".
  if (bayt === null) return "—";
  if (bayt < 1024) return `${bayt} B`;
  if (bayt < 1024 * 1024) return `${(bayt / 1024).toFixed(1)} KB`;
  return `${(bayt / 1024 / 1024).toFixed(1)} MB`;
}

function tarih(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Yedek() {
  const qc = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["yedek-konsolu"],
    queryFn: yedekKonsolu,
  });

  const yedekAl = useMutation({
    mutationFn: () => elleYedekAl(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["yedek-konsolu"] }),
  });

  const tatbikat = useMutation({
    mutationFn: () => yedekTatbikati(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["yedek-konsolu"] }),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Yedek konsolu açılamadı"
        aciklama={
          error instanceof ApiHatasi
            ? error.message
            : "Sunucuya ulaşılamadı. Bu, yedeklerin alınmadığı anlamına gelmez — konsol okunamadı."
        }
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const s = data.saglik;

  return (
    <div className="flex flex-col gap-6">
      <Kart>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-notr-900 text-sm font-semibold">Yedekleme durumu</h3>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium",
                  SAGLIK_SINIFI[s.saglik],
                )}
              >
                {SAGLIK_ADI[s.saglik]}
              </span>
            </div>
            <p className="text-notr-700 mt-1 text-sm">{s.sebep}</p>
          </div>

          <div className="flex shrink-0 gap-2">
            <Buton gorunum="ikincil" onClick={() => yedekAl.mutate()} disabled={yedekAl.isPending}>
              {yedekAl.isPending ? "Alınıyor…" : "Elle yedek al"}
            </Buton>
            <Buton
              gorunum="ikincil"
              onClick={() => tatbikat.mutate()}
              disabled={tatbikat.isPending}
            >
              {tatbikat.isPending ? "Tatbikat sürüyor…" : "Tatbikat yap"}
            </Buton>
          </div>
        </div>

        {/*
          Tatbikat UYARIYLA acikliniyor: dugmenin adi "geri yukle"ye
          benziyor ve bir yoneticinin uretime dokundugunu sanmasi
          isten degil. Ne yaptigini yaziyoruz.
        */}
        <p className="rounded-alan bg-notr-50 text-notr-600 mt-3 p-3 text-xs">
          <strong>Tatbikat üretime dokunmaz:</strong> yedeği geçici bir veritabanına yükler, ölçer
          ve düşürür. Ölçülen süre gerçek geri dönüş süresidir — söz verilen değil.
        </p>

        {yedekAl.isError && (
          <p className="text-hata-500 mt-2 text-xs">
            Elle yedek başarısız:{" "}
            {yedekAl.error instanceof ApiHatasi ? yedekAl.error.message : "bilinmeyen hata"}
          </p>
        )}
        {tatbikat.isError && (
          <p className="text-hata-500 mt-2 text-xs">
            Tatbikat başarısız:{" "}
            {tatbikat.error instanceof ApiHatasi ? tatbikat.error.message : "bilinmeyen hata"}
          </p>
        )}

        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Olcum
            baslik="Son yedek"
            deger={
              s.sonYedekYasiSaat === null ? "—" : `${Math.floor(s.sonYedekYasiSaat)} saat önce`
            }
          />
          <Olcum
            baslik="Doğrulanmamış"
            deger={`${s.dogrulanmamisSayisi} koşu`}
            vurgu={s.dogrulanmamisSayisi > 0}
          />
          <Olcum
            baslik="Son tam tatbikat"
            deger={data.sonTatbikat ? tarih(data.sonTatbikat.tarih) : "Hiç yapılmadı"}
            vurgu={data.tatbikatGecikmisMi}
          />
          {/*
            Olculen RTO — "hedefimiz 4 saat" degil, SON TATBIKATTA ne
            surdugu. Hedef yazsaydik ekran bir vaadi olcum diye
            gosterirdi.
          */}
          <Olcum
            baslik="Ölçülen geri dönüş"
            deger={data.olculenRtoSn === null ? "Ölçülmedi" : `${data.olculenRtoSn} sn`}
          />
        </dl>

        {data.tatbikatGecikmisMi && (
          <p className="rounded-alan bg-uyari-500/10 text-uyari-600 mt-3 p-3 text-xs">
            {
              data.sonTatbikat === null
                ? "Hiç tam tatbikat yapılmamış. Denenmemiş bir yedeğin geri yüklenip yüklenemeyeceği bilinmiyor."
                : "Son tam tatbikatın üzerinden bir aydan fazla geçti. Şartname aylık tatbikat istiyor." /* F-YED-01 */
            }
          </p>
        )}
      </Kart>

      {(data.hedefUyarilari.length > 0 || data.zarfsizAnahtarVar) && (
        <section className="flex flex-col gap-2">
          <h3 className="text-notr-900 text-sm font-semibold">Yapılandırma uyarıları</h3>
          <ul className="flex flex-col gap-2">
            {data.hedefUyarilari.map((u) => (
              <li
                key={u.kod}
                className="rounded-kart shadow-kart border-uyari-500/30 bg-uyari-500/10 text-notr-700 border p-3 text-sm"
              >
                {u.aciklama}
              </li>
            ))}
            {/*
              Anahtar zarfa konulmamissa yedek okunamaz. Yedegin
              varligi tek basina hicbir sey ifade etmiyor — bu yuzden
              uyarilarin en ustunde degil, ayni listede ama ayri
              cumleyle duruyor.
            */}
            {data.zarfsizAnahtarVar && (
              <li className="rounded-kart shadow-kart border-hata-500/30 bg-hata-500/10 text-notr-700 border p-3 text-sm">
                Kurtarma zarfına konulduğu onaylanmamış aktif bir şifreleme anahtarı var. Anahtar
                kaybolursa yedekler <strong>açılamaz</strong>; dosyaların duruyor olması geri
                dönülebileceği anlamına gelmez{/* F-YED-18 */}.
              </li>
            )}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Hedefler ({data.hedefler.length})</h3>
        <ul className="flex flex-col gap-2">
          {data.hedefler.map((h) => (
            <li key={h.id}>
              <HedefKarti hedef={h} />
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Son koşular ({data.kosular.length})</h3>
        {data.kosular.length === 0 ? (
          <p className="rounded-kart border-notr-200 bg-notr-0 shadow-kart text-notr-600 border p-4 text-sm">
            Son 30 günde hiç koşu yok. Zamanlanmış işin (<code>pnpm scheduler</code>) ayakta olup
            olmadığını kontrol edin.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.kosular.map((k) => (
              <li key={k.id}>
                <KosuKarti kosu={k} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.artikVeritabanlari.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-notr-900 text-sm font-semibold">
            Artakalan veritabanları ({data.artikVeritabanlari.length})
          </h3>
          {/*
            SISTEM BUNLARI SILMIYOR ve sebebi yaziyor. Yazmasaydik
            "neden hâlâ duruyor" sorusu, birinin elle silmesiyle
            cevaplanirdi — ve silinen sey, kotu bir geri yuklemeden
            donulecek tek yer olabilirdi.
          */}
          <p className="rounded-alan bg-notr-50 text-notr-600 p-3 text-xs">
            Bunlar <strong>kendiliğinden silinmez</strong>. <code>_eski_</code> ile biten
            veritabanı, geri yükleme öncesindeki hâldir: takastan sonra “bu yedek yanlışmış” denirse
            dönülecek tek yer orasıdır. Silme kararı sizin — yeterince zaman geçtiğine emin
            olduğunuzda veritabanı sunucusundan elle kaldırın.
          </p>
          <ul className="flex flex-col gap-1">
            {data.artikVeritabanlari.map((v) => (
              <Kart key={v.ad} olarak="li" className="p-3 text-sm">
                <span className="text-notr-900 font-mono text-xs">{v.ad}</span>{" "}
                <span className="text-notr-600 text-xs">
                  · {v.boyut} ·{" "}
                  {v.tur === "GERI_YUKLEME_ONCESI"
                    ? "geri yükleme öncesi — dönülecek tek yer"
                    : v.tur === "TATBIKAT"
                      ? "tatbikat kalıntısı"
                      : "diğer"}
                </span>
              </Kart>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">
          Şifreleme anahtarları ({data.anahtarlar.length})
        </h3>
        {/*
          Anahtarin KENDISI hicbir zaman ekrana gelmiyor; sunucu
          yalnizca parmak izini gonderiyor. Anahtari gosteren bir
          konsol, konsolu acabilen herkese yedeklerin icerigini
          acardi.
        */}
        <ul className="flex flex-col gap-2">
          {data.anahtarlar.map((a) => (
            <Kart key={a.id} olarak="li" className="text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-notr-900 font-medium">{a.etiket}</span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-medium",
                    a.zarftaMi
                      ? "bg-basarili-500/15 text-onay-700"
                      : "bg-hata-500/15 text-hata-700",
                  )}
                >
                  {a.zarftaMi ? "Zarfta" : "Zarfa konulmadı"}
                </span>
              </div>
              <p className="text-notr-600 mt-1 text-xs">
                {a.algoritma} · parmak izi {a.parmakIzi} · {a.aktif ? "aktif" : "pasif"}
              </p>
            </Kart>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Olcum({
  baslik,
  deger,
  vurgu = false,
}: {
  baslik: string;
  deger: string;
  vurgu?: boolean;
}) {
  return (
    <div>
      <dt className="text-notr-600 text-xs">{baslik}</dt>
      <dd
        className={cn(
          "text-sm font-medium tabular-nums",
          vurgu ? "text-uyari-600" : "text-notr-900",
        )}
      >
        {deger}
      </dd>
    </div>
  );
}

function HedefKarti({ hedef }: { hedef: YedekHedefi }) {
  return (
    <Kart>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-notr-900 text-sm font-medium">{hedef.ad}</span>
        {!hedef.aktif && <Rozet>Pasif</Rozet>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Etiket acik={hedef.siteDisi} metin="Site dışı" kapaliMetin="Aynı konum" />
        <Etiket acik={hedef.degismez} metin="Değişmez" kapaliMetin="Silinebilir" />
        <Etiket acik={hedef.yalnizYazma} metin="Yalnız yazma" kapaliMetin="Silme yetkili" />
      </div>
      <p className="text-notr-600 mt-2 text-xs">
        {hedef.tur} · son yazma {tarih(hedef.sonYazma)} · kullanılan {boyut(hedef.kullanilanBayt)}
      </p>
    </Kart>
  );
}

/**
 * Ozellik KAPALIYSA da yaziliyor.
 *
 * Yalnizca acik olanlari gostermek "site disi" rozetinin yoklugunu
 * gormeye baglardi; olmayan bir seyi fark etmek, yazan bir seyi
 * okumaktan cok daha zor.
 */
function Etiket({
  acik,
  metin,
  kapaliMetin,
}: {
  acik: boolean;
  metin: string;
  kapaliMetin: string;
}) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs",
        // `notr-600`: `notr-500` bu zeminde 4,41 veriyor, AA altinda.
        // Bu hap bir SISTEM DURUMU soyluyor; okunamayan bir durum
        // rozeti, olmayan bir rozetten daha kotu.
        acik ? "bg-basarili-500/15 text-onay-700" : "bg-notr-100 text-notr-600",
      )}
    >
      {acik ? metin : kapaliMetin}
    </span>
  );
}

function KosuKarti({ kosu }: { kosu: YedekKosusu }) {
  /**
   * ================================================================
   * UC AYRI HAL, UC AYRI METIN
   * ================================================================
   * `dogrulandiMi === null` "denenmedi", `false` "denendi ve
   * olmadi". Ikisini tek bir "dogrulanmadi" altinda toplamak,
   * dogrulama isinin hic kosmadigini gizlerdi.
   */
  const dogrulama =
    kosu.dogrulandiMi === null
      ? { metin: "Doğrulanmadı", sinif: "bg-uyari-500/15 text-uyari-800" }
      : kosu.dogrulandiMi
        ? {
            metin: `Doğrulandı${kosu.dogrulamaSeviyesi ? ` · ${kosu.dogrulamaSeviyesi}` : ""}`,
            sinif: "bg-basarili-500/15 text-onay-700",
          }
        : { metin: "Doğrulama BAŞARISIZ", sinif: "bg-hata-500/15 text-hata-700" };

  return (
    <Kart>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-notr-900 text-sm font-medium">
            {tarih(kosu.basladi)}
            {kosu.elleMi && <span className="text-notr-600 ml-2 text-xs">elle</span>}
          </p>
          <p className="text-notr-600 mt-0.5 text-xs">
            {kosu.isAdi} → {kosu.hedefAdi} · {boyut(kosu.boyutBayt)}
            {kosu.sureSn !== null && ` · ${kosu.sureSn} sn`}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-1.5">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              kosu.durum === "SUCCEEDED"
                ? "bg-basarili-500/15 text-onay-700"
                : kosu.durum === "FAILED"
                  ? "bg-hata-500/15 text-hata-700"
                  : "bg-notr-100 text-notr-600",
            )}
          >
            {kosu.durum}
          </span>
          <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", dogrulama.sinif)}>
            {dogrulama.metin}
          </span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <Etiket acik={kosu.uzakKopyaVar} metin="Uzak kopya var" kapaliMetin="UZAK KOPYA YOK" />
        {kosu.suresiDoldu && (
          /*
            Suresi dolan kosu listede kaliyor ama ACIKCA isaretli:
            "basarili" gorunup dosyasi olmayan bir satir, geri
            donulebilecegi izlenimi verirdi.
          */
          <Rozet>Süresi doldu — dosya yok</Rozet>
        )}
      </div>

      {kosu.hata !== null && <p className="text-hata-500 mt-2 text-xs">{kosu.hata}</p>}

      <DogrulamaGecmisi kosuId={kosu.id} />
    </Kart>
  );
}

/**
 * ÜÇ KADEMELİ DOĞRULAMA GEÇMİŞİ — F-YED-01
 *
 * `GET /yedek/kosu/:id/dogrulamalar` yazılıydı, hiçbir ekran
 * çağırmıyordu. Koşu kartı yalnızca "doğrulandı / doğrulanmadı"
 * özetini gösteriyordu; HANGİ kademenin koştuğu, ne kadar sürdüğü
 * ve hangisinin düştüğü görünmüyordu.
 *
 * ================================================================
 * ÜÇ KADEME, ÜÇ AYRI GÜVEN SEVİYESİ
 * ================================================================
 * `CHECKSUM` dosyanın bozulmadığını söyler; `OPENABLE` açılabildiğini;
 * `FULL_DRILL` gerçekten geri yüklenebildiğini. Yalnızca ilkini
 * geçmiş bir yedek "doğrulandı" sayılır ama geri dönülebileceğini
 * KANITLAMAZ — bu ekran farkı gösteriyor.
 *
 * `basarili: null` "denenmedi YA DA hâlâ sürüyor" demek ve
 * "başarısız" diye gösterilmiyor: yedeğin bozuk olduğunu sanmak,
 * bozuk olduğunu bilmemekten farklı bir hata üretir.
 */
function DogrulamaGecmisi({ kosuId }: { kosuId: string }) {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["yedek-dogrulamalari", kosuId],
    queryFn: () => yedekDogrulamalari(kosuId),
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div className="mt-2">
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Doğrulama geçmişi
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 mt-2 flex flex-col gap-2 border p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-notr-800 text-sm font-semibold">Doğrulama geçmişi</h4>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {error instanceof ApiHatasi ? error.message : "Doğrulamalar alınamadı."}
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(
              [
                ["CHECKSUM", "Sağlama toplamı", "Dosya bozulmadı"],
                ["OPENABLE", "Açılabilirlik", "Arşiv açıldı"],
                ["FULL_DRILL", "Tam tatbikat", "Gerçekten geri yüklendi"],
              ] as const
            ).map(([anahtar, ad, ne]) => {
              const k = data.kademeler[anahtar];
              return (
                <div key={anahtar} className="rounded-alan bg-notr-50 p-2">
                  <dt className="text-notr-700 text-xs font-medium">{ad}</dt>
                  <dd className="mt-0.5 text-xs">
                    {!k.denendi ? (
                      <span className="text-notr-600">denenmedi</span>
                    ) : k.basarili === null ? (
                      /*
                        `null` = denendi ama sonuc YOK: ya suruyor ya
                        yarim kaldi. "Basarisiz" demek, kosmakta olan
                        bir dogrulamayi arizali gostermek olurdu.
                      */
                      <span className="text-uyari-600">sürüyor ya da yarım kaldı</span>
                    ) : k.basarili ? (
                      <span className="text-onay-700">geçti</span>
                    ) : (
                      <span className="text-hata-700">DÜŞTÜ</span>
                    )}
                  </dd>
                  <p className="text-notr-600 mt-0.5 text-xs">{ne}</p>
                </div>
              );
            })}
          </dl>

          {data.gecmis.length === 0 ? (
            <p className="text-notr-600 text-xs">Bu koşuda hiç doğrulama çalıştırılmamış.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-xs">
              {data.gecmis.map((g) => (
                <li key={g.id} className="flex flex-wrap justify-between gap-2">
                  <span className="text-notr-700">
                    {g.level} · {anMetni(g.startedAt)}
                  </span>
                  <span
                    className={
                      g.isOk === null ? "text-notr-600" : g.isOk ? "text-onay-700" : "text-hata-700"
                    }
                  >
                    {g.isOk === null ? "sonuçsuz" : g.isOk ? "geçti" : "düştü"}
                    {g.restoreSec !== null && ` · ${g.restoreSec} sn`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {!data.saglamaToplamiVar && (
            <p role="alert" className="text-uyari-600 text-xs">
              Bu koşuda sağlama toplamı YOK — dosyanın bozulup bozulmadığı hiç ölçülemez.
            </p>
          )}
        </>
      )}
    </div>
  );
}
